import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';

interface EffectifCardProps {
  cuisineMidi: number;
  salleMidi: number;
  cuisineSoir: number;
  salleSoir: number;
  cuisineMidiScheduled?: number;
  salleMidiScheduled?: number;
  cuisineSoirScheduled?: number;
  salleSoirScheduled?: number;
}

export default function EffectifCard({ 
  cuisineMidi, 
  salleMidi, 
  cuisineSoir, 
  salleSoir,
  cuisineMidiScheduled,
  salleMidiScheduled,
  cuisineSoirScheduled,
  salleSoirScheduled
}: EffectifCardProps) {
  const theme = useTheme();
  const border = theme?.palette?.divider ;
  
  const totalMidi = cuisineMidi + salleMidi;
  const totalSoir = cuisineSoir + salleSoir;
  const totalGeneral = totalMidi + totalSoir;

  const totalMidiScheduled = (cuisineMidiScheduled ?? 0) + (salleMidiScheduled ?? 0);
  const totalSoirScheduled = (cuisineSoirScheduled ?? 0) + (salleSoirScheduled ?? 0);
  const totalGeneralScheduled = totalMidiScheduled + totalSoirScheduled;

  const hasScheduledData = cuisineMidiScheduled !== undefined;

  return (
    <Box className='rounded-xl p-6 border transition' sx={{ borderColor: "divider", backgroundColor: "background.paper", color:"text.primary" }}>
      <div className='grid grid-cols-2 gap-6'>
        {/* Service Midi */}
        <div>
          <span className={`text-sm font-semibold block mb-4`}>Service Midi</span>
          <div className='space-y-3'>
            <div className='flex items-center justify-between'>
              <span className={`text-sm`}>En cuisine</span>
              <div className="flex items-baseline gap-2">
                {hasScheduledData && (
                  <span className="text-xl font-bold" style={{ color: cuisineMidiScheduled === cuisineMidi ? theme.palette.success.main : cuisineMidiScheduled! < cuisineMidi ? theme.palette.error.main : theme.palette.warning.main }}>
                    {cuisineMidiScheduled}
                  </span>
                )}
                <span className={`text-sm ${hasScheduledData ? 'opacity-50' : 'text-2xl font-bold'}`}>
                  {hasScheduledData ? `/ ${cuisineMidi}` : cuisineMidi}
                </span>
              </div>
            </div>
            <div className={`h-px`} style={{ backgroundColor: border }}></div>
            <div className='flex items-center justify-between'>
              <span className={`text-sm`}>En salle</span>
              <div className="flex items-baseline gap-2">
                {hasScheduledData && (
                  <span className="text-xl font-bold" style={{ color: salleMidiScheduled === salleMidi ? theme.palette.success.main : salleMidiScheduled! < salleMidi ? theme.palette.error.main : theme.palette.warning.main }}>
                    {salleMidiScheduled}
                  </span>
                )}
                <span className={`text-sm ${hasScheduledData ? 'opacity-50' : 'text-2xl font-bold'}`}>
                  {hasScheduledData ? `/ ${salleMidi}` : salleMidi}
                </span>
              </div>
            </div>
            <div className={`pt-3 border-t`} style={{ borderColor: border }}>
              <div className='flex items-center justify-between'>
                <span className={`text-xs font-bold`}>TOTAL</span>
                <div className="flex items-baseline gap-2">
                  {hasScheduledData && (
                    <span className="text-xl font-bold" style={{ color: totalMidiScheduled === totalMidi ? theme.palette.success.main : totalMidiScheduled < totalMidi ? theme.palette.error.main : theme.palette.warning.main }}>
                      {totalMidiScheduled}
                    </span>
                  )}
                  <span className={`text-sm ${hasScheduledData ? 'opacity-50' : 'text-2xl font-bold'}`}>
                    {hasScheduledData ? `/ ${totalMidi}` : totalMidi}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Service Soir */}
        <div>
          <span className={`text-sm font-semibold block mb-4`}>Service Soir</span>
          <div className='space-y-3'>
            <div className='flex items-center justify-between'>
              <span className={`text-sm`}>En cuisine</span>
              <div className="flex items-baseline gap-2">
                {hasScheduledData && (
                  <span className="text-xl font-bold" style={{ color: cuisineSoirScheduled === cuisineSoir ? theme.palette.success.main : cuisineSoirScheduled! < cuisineSoir ? theme.palette.error.main : theme.palette.warning.main }}>
                    {cuisineSoirScheduled}
                  </span>
                )}
                <span className={`text-sm ${hasScheduledData ? 'opacity-50' : 'text-2xl font-bold'}`}>
                  {hasScheduledData ? `/ ${cuisineSoir}` : cuisineSoir}
                </span>
              </div>
            </div>
            <div className={`h-px`} style={{ backgroundColor: border }}></div>
            <div className='flex items-center justify-between'>
              <span className={`text-sm`}>En salle</span>
              <div className="flex items-baseline gap-2">
                {hasScheduledData && (
                  <span className="text-xl font-bold" style={{ color: salleSoirScheduled === salleSoir ? theme.palette.success.main : salleSoirScheduled! < salleSoir ? theme.palette.error.main : theme.palette.warning.main }}>
                    {salleSoirScheduled}
                  </span>
                )}
                <span className={`text-sm ${hasScheduledData ? 'opacity-50' : 'text-2xl font-bold'}`}>
                  {hasScheduledData ? `/ ${salleSoir}` : salleSoir}
                </span>
              </div>
            </div>
            <div className={`pt-3 border-t`} style={{ borderColor: border }}>
              <div className='flex items-center justify-between'>
                <span className={`text-xs font-bold`}>TOTAL</span>
                <div className="flex items-baseline gap-2">
                  {hasScheduledData && (
                    <span className="text-xl font-bold" style={{ color: totalSoirScheduled === totalSoir ? theme.palette.success.main : totalSoirScheduled < totalSoir ? theme.palette.error.main : theme.palette.warning.main }}>
                      {totalSoirScheduled}
                    </span>
                  )}
                  <span className={`text-sm ${hasScheduledData ? 'opacity-50' : 'text-2xl font-bold'}`}>
                    {hasScheduledData ? `/ ${totalSoir}` : totalSoir}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Total Général */}
      <div className={`mt-6 pt-4 border-t`} style={{ borderColor: border }}>
        <div className='flex items-center justify-between'>
          <span className={`text-sm font-bold`}>TOTAL GÉNÉRAL</span>
          <div className="flex items-baseline gap-2">
            {hasScheduledData && (
              <span className="text-2xl font-bold" style={{ color: totalGeneralScheduled === totalGeneral ? theme.palette.success.main : totalGeneralScheduled < totalGeneral ? theme.palette.error.main : theme.palette.warning.main }}>
                {totalGeneralScheduled}
              </span>
            )}
            <span className={`text-lg ${hasScheduledData ? 'opacity-50' : 'text-3xl font-bold'}`}>
              {hasScheduledData ? `/ ${totalGeneral}` : totalGeneral}
            </span>
          </div>
        </div>
      </div>

      {hasScheduledData && (
        <div className="mt-4 text-xs opacity-70 text-center">
          <span style={{ color: theme.palette.success.main }}>●</span> Conforme
          <span className="mx-2">|</span>
          <span style={{ color: theme.palette.error.main }}>●</span> Sous-effectif
          <span className="mx-2">|</span>
          <span style={{ color: theme.palette.warning.main }}>●</span> Sur-effectif
        </div>
      )}
    </Box>
  );
}
